import type { ArcProperties, VoiceSpec } from './types';
import { voiceBundle, ROWS_PER_VOICE, STYLE_DIM } from './voiceBundle';

function lerpVec(a: Float32Array, b: Float32Array, t: number): Float32Array {
  const out = new Float32Array(a.length);
  const u = 1 - t;
  for (let i = 0; i < a.length; i++) out[i] = a[i] * u + b[i] * t;
  return out;
}

/**
 * Mirror of the desktop `resolve_style_quadratic`:
 *   1. Warp spec.t through a power curve (exponent = arc.curveWarp * 2).
 *   2. Apply arc.spectralSkew as an asymmetric tilt on the warped position.
 *   3. Primary lerp between rowA and rowB at the skewed position.
 *   4. Harmonic blend toward (rowA + rowB) * 0.5 weighted by harmonicWeight.
 */
export function resolveBlendedStyle(
  spec: VoiceSpec,
  arc: ArcProperties,
  tokenCount: number,
): Float32Array {
  const rows = voiceBundle.getRows();
  if (!rows) throw new Error('voice bundle not loaded');
  const rowIdx = Math.min(Math.max(tokenCount - 2, 0), ROWS_PER_VOICE - 1);
  const rowA = rows[spec.voiceA][rowIdx];
  const rowB = rows[spec.voiceB][rowIdx];

  // Self-blend shortcut — preserves bitwise identity with Stage 7 pins.
  if (spec.voiceA === spec.voiceB) {
    return new Float32Array(rowA);
  }

  // 1. Curve warp on t.
  const k = arc.curveWarp * 2.0;
  const tClamped = Math.min(Math.max(spec.t, 1e-6), 1 - 1e-6);
  const tPow = Math.pow(tClamped, k);
  const oPow = Math.pow(1 - tClamped, k);
  const warpedT = tPow / (tPow + oPow);

  // 2. Spectral skew.
  const skewedT = Math.min(Math.max(warpedT + arc.spectralSkew, 0), 1);

  // 3. Primary lerp.
  const primary = lerpVec(rowA, rowB, skewedT);

  // 4. Harmonic blend toward the voice-pair mean.
  if (arc.harmonicWeight < 1e-4) return primary;
  const mean = new Float32Array(STYLE_DIM);
  for (let i = 0; i < STYLE_DIM; i++) mean[i] = (rowA[i] + rowB[i]) * 0.5;
  return lerpVec(primary, mean, arc.harmonicWeight);
}
